import { resetPrefs } from "@/hooks/usePref";
import React, { useState, useEffect, createContext, useContext } from "react";
import axios, { API } from "@/lib/api";
import { setCurSym } from "@/utils/cur";

// per-shop display currency symbol ($ / ¥) — refresh right after any login so the UI switches at once
const loadCurSym = () => axios.get(`${API}/settings/system`, { skipGlobalError: true, timeout: 8000 })
  .then(r => setCurSym(r.data?.currency_symbol)).catch(() => {});

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState(null);
  // 🛡️ Per-tab identity: prefer sessionStorage (per-tab) over localStorage
  // (shared across tabs). This prevents the "log into 2 tenants in 2 tabs,
  // tab 1's POS → back to admin lands in tab 2's tenant" bug. localStorage
  // is kept as a fallback for offline recovery / 90-day persistent sessions
  // (price_checker, desktop wrapper).
  const [token, setToken] = useState(sessionStorage.getItem("token") || localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Migrate old localStorage tokens (non price_checker)
    // Keep localStorage token only if it's a price_checker persistent session
  }, []);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const res = await axios.get(`${API}/auth/me`);
      const userData = res.data;
      // Normalize tenant_id: the server may omit the field for super admins;
      // force it to null so frontend `AdminRoute` superOnly check is deterministic.
      if (userData && userData.tenant_id === undefined) {
        userData.tenant_id = null;
      }
      setUser(userData);
    loadCurSym();
      // Cache user for offline access
      localStorage.setItem("cached_auth_user", JSON.stringify(userData));
      try {
        const permRes = await axios.get(`${API}/auth/permissions`);
        setPermissions(permRes.data);
        localStorage.setItem("cached_auth_permissions", JSON.stringify(permRes.data));
      } catch {}
    } catch (e) {
      // Only logout on explicit server rejection (401/403)
      if (e.response?.status === 401 || e.response?.status === 403) {
        logout();
      } else {
        // Network error (offline/timeout) — use cached user data.
        // Prefer per-tab sessionStorage over shared localStorage.
        const cachedUser = sessionStorage.getItem("cached_auth_user") || localStorage.getItem("cached_auth_user");
        if (cachedUser) {
          setUser(JSON.parse(cachedUser));
          const cachedPerms = localStorage.getItem("cached_auth_permissions");
          if (cachedPerms) setPermissions(JSON.parse(cachedPerms));
        } else {
          // No cache available — check pos_user as last resort
          // DELIBERATELY DON'T use pos_user here — it's a cashier/pos role.
          // AdminRoute checks user role and will redirect to /pos, confusing the user.
          // Don't logout — leave user null, PosPage has its own session restore
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const login = async (username, password, extra = {}) => {
    // Desktop/Electron wrapper → request a 90-day token so the merchant can
    // run offline for extended periods without forced re-login.
    const remember = !!(typeof window !== "undefined" && (window.selloxDesktop || window.sellox?.isDesktop));
    const res = await axios.post(`${API}/auth/login`, { username, password, remember, ...extra });
    const { token: newToken, user: userData } = res.data;
    // Make sure super-admin login does NOT inherit stale tenant_id from a prior
    // tenant-login cache in this browser. Force-clear it on the user object.
    if (userData && userData.tenant_id === undefined) {
      userData.tenant_id = null;
    }
    // 🛡️ Per-tab identity: sessionStorage first so 2 tabs can hold 2 identities
    sessionStorage.setItem("token", newToken);
    sessionStorage.setItem("cached_auth_user", JSON.stringify(userData));
    localStorage.setItem("token", newToken);
    localStorage.setItem("cached_auth_user", JSON.stringify(userData));
    axios.defaults.headers.common["Authorization"] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);
    loadCurSym();
    try {
      const permRes = await axios.get(`${API}/auth/permissions`);
      setPermissions(permRes.data);
      localStorage.setItem("cached_auth_permissions", JSON.stringify(permRes.data));
    } catch {}
    return userData;
  };

  const logout = () => {
    setCurSym("$");
    resetPrefs();
    sessionStorage.removeItem("token");
    sessionStorage.removeItem("cached_auth_user");
    localStorage.removeItem("token");
    localStorage.removeItem("cached_auth_user");
    localStorage.removeItem("cached_auth_permissions");
    // NOTE: pos_offline_session and pos_auth_creds are deliberately KEPT so the
    // cashier can log back in while the internet is down (store kiosk scenario).
    delete axios.defaults.headers.common["Authorization"];
    setToken(null);
    setUser(null);
    setPermissions(null);
  };

  // Offline login: restore a previously-saved session without any server call.
  const offlineLogin = (userData, tok) => {
    if (tok) {
      sessionStorage.setItem("token", tok);
      localStorage.setItem("token", tok);
      axios.defaults.headers.common["Authorization"] = `Bearer ${tok}`;
      setToken(tok);
    }
    sessionStorage.setItem("cached_auth_user", JSON.stringify(userData));
    localStorage.setItem("cached_auth_user", JSON.stringify(userData));
    setUser(userData);
    loadCurSym();
    try {
      const cachedPerms = localStorage.getItem("cached_auth_permissions");
      if (cachedPerms) setPermissions(JSON.parse(cachedPerms));
    } catch {}
    setLoading(false);
    return userData;
  };

  const tenantLogin = async (tenantId, username, password, extra = {}) => {
    const remember = !!(typeof window !== "undefined" && window.sellox?.isDesktop);
    // 15s cap: when the server is unreachable (ISP fine, Sellox down) fall to offline login fast instead of hanging minutes
    const res = await axios.post(`${API}/auth/tenant-login`, { tenant_id: tenantId, username, password, remember, ...extra }, { timeout: 15000 });
    const { token: newToken, user: userData, tenant } = res.data;
    userData.tenant_id = tenantId;
    userData.tenant_name = tenant?.name || '';
    userData.tenant = { plan: tenant?.plan, is_trial: !!tenant?.is_trial, lifetime_active: !!tenant?.lifetime_active, has_online_shop: tenant?.has_online_shop !== false };
    // 🛡️ Always write to sessionStorage FIRST (per-tab) so two tabs logged
    // into two different tenants do not bleed into each other. localStorage
    // is still set for offline/PWA fallback.
    sessionStorage.setItem("token", newToken);
    sessionStorage.setItem("cached_auth_user", JSON.stringify(userData));
    localStorage.setItem("cached_auth_user", JSON.stringify(userData));
    localStorage.setItem("token", newToken);
    axios.defaults.headers.common["Authorization"] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);
    loadCurSym();
    // Persistent offline snapshot — survives logout so the next offline login works
    try {
      localStorage.setItem("pos_offline_session", JSON.stringify({ user: userData, token: newToken, ts: Date.now() }));
      const smap = JSON.parse(localStorage.getItem("pos_offline_sessions") || "{}");
      smap[`${(tenantId || '').toLowerCase()}:${(username || '').toLowerCase()}`] = { user: userData, token: newToken, ts: Date.now() };
      localStorage.setItem("pos_offline_sessions", JSON.stringify(smap));
    } catch {}
    try {
      const permRes = await axios.get(`${API}/auth/permissions`);
      setPermissions(permRes.data);
      localStorage.setItem("cached_auth_permissions", JSON.stringify(permRes.data));
    } catch {}
    return { user: userData, tenant };
  };

  // Adopt an externally-obtained session (POS quick user switch) as the GLOBAL identity
  // so 进入后台/AdminRoute sees the newly switched user, not the previous one.
  const adoptSession = async (newToken, userData) => {
    if (!newToken || !userData) return;
    if (userData.tenant_id === undefined) userData.tenant_id = null;
    sessionStorage.setItem("token", newToken);
    sessionStorage.setItem("cached_auth_user", JSON.stringify(userData));
    localStorage.setItem("token", newToken);
    localStorage.setItem("cached_auth_user", JSON.stringify(userData));
    axios.defaults.headers.common["Authorization"] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);
    loadCurSym();
    try {
      const permRes = await axios.get(`${API}/auth/permissions`, { headers: { Authorization: `Bearer ${newToken}` } });
      setPermissions(permRes.data);
      localStorage.setItem("cached_auth_permissions", JSON.stringify(permRes.data));
    } catch {}
  };

  // Helper: check if user can access admin pages
  const canAccessAdmin = () => {
    if (!user) return false;
    const role = permissions?.role || user?.role;
    // admin, owner, manager, warehouse, finance can access admin panel
    return ["admin", "owner", "manager", "warehouse", "finance", "wholesale_clerk"].includes(role);
  };

  // Helper: check specific permission
  const hasPermission = (perm) => {
    if (!permissions) return false;
    return permissions.permissions?.[perm] === true;
  };

  return (
    <AuthContext.Provider value={{ user, token, permissions, login, tenantLogin, offlineLogin, adoptSession, logout, loading, canAccessAdmin, hasPermission }}>
      {children}
    </AuthContext.Provider>
  );
};
